import { useState, useEffect } from "react";
import { ArrowRight, Sparkles, Shield, Eye, Compass, Magnet } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCALE = [
  { v: 1, short: "Almost never", label: "I can't remember the last time" },
  { v: 2, short: "Once a month", label: "Once in the last month or two" },
  { v: 3, short: "A few times a month", label: "A few times this month" },
  { v: 4, short: "Most weeks", label: "Most weeks" },
  { v: 5, short: "Most days", label: "Most days" },
];

const GROUPS = [
  {
    key: "pleasure",
    ingredient: "Pleasure",
    subtitle: "Do you taste your life, or move past it?",
    prompts: [
      "When did you last taste your food — really taste it — without doing anything else at the same time?",
      "When did you last do something purely because it felt good, before the list was finished?",
      "When did you last let yourself enjoy something without first earning it?",
    ],
  },
  {
    key: "rest",
    ingredient: "Rest",
    subtitle: "Real restoration, or collapse-and-recover?",
    prompts: [
      "When did you last rest before you were depleted, rather than after you crashed?",
      "When did you last have downtime that wasn't scrolling and wasn't recovery from being wrecked?",
      "When did you last wake up feeling something had actually been restored?",
    ],
  },
  {
    key: "beauty",
    ingredient: "Beauty",
    subtitle: "Do you let it in daily, or is it a someday thing?",
    prompts: [
      "When did you last stop — actually stop — for something beautiful in an ordinary place?",
      "When did you last choose the more beautiful option over the faster one?",
      "When did you last seek beauty out on purpose at home: music, art, the long way round?",
    ],
  },
  {
    key: "connection",
    ingredient: "Connection",
    subtitle: "Truly seen — in the hard moments and the good ones?",
    prompts: [
      "When did you last share a meal with people where nobody was rushing to be anywhere?",
      "When did you last tell someone about a small win — and let yourself enjoy them being glad for you?",
      "When did you last let someone see you as you actually were, good or hard, without editing it first?",
    ],
  },
  {
    key: "aliveness",
    ingredient: "Aliveness & Desire",
    subtitle: "Lit up and curious, or running on obligation?",
    prompts: [
      "When did you last want something for no reason other than that you wanted it?",
      "When did you last let yourself be surprised or delighted — and stop for it?",
      "When did you last feel real energy in your body that wasn't caffeine or adrenaline?",
    ],
  },
];

function Wheel({ ingredients }) {
  const size = 320;
  const cx = size / 2;
  const cy = size / 2;
  const R = 108;
  const n = ingredients.length;
  const pt = (i, v) => {
    const angle = (-90 + (i * 360) / n) * (Math.PI / 180);
    const r = (R * v) / 5;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };
  const poly = (v) => ingredients.map((ing, i) => pt(i, typeof v === "function" ? v(ing) : v).join(",")).join(" ");
  const scorePoly = ingredients.map((ing, i) => pt(i, ing.score).join(",")).join(" ");

  return (
    <svg viewBox={`-36 -12 ${size + 72} ${size + 40}`} className="w-full max-w-[360px] mx-auto" data-testid="audit-wheel">
      {/* concentric guides */}
      {[1, 2, 3, 4, 5].map((ring) => (
        <polygon key={ring} points={poly(ring)} fill="none" stroke="#2C2A28" strokeOpacity="0.08" strokeWidth="1" />
      ))}
      {/* spokes */}
      {ingredients.map((ing, i) => {
        const [x, y] = pt(i, 5);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#2C2A28" strokeOpacity="0.08" strokeWidth="1" />;
      })}
      {/* outer max (dotted) */}
      <polygon points={poly(5)} fill="none" stroke="#B85C38" strokeOpacity="0.6" strokeWidth="1.5" strokeDasharray="3 4" />
      {/* their score (gold) */}
      <polygon points={scorePoly} fill="#C9A24B" fillOpacity="0.28" stroke="#C9A24B" strokeWidth="2" />
      {ingredients.map((ing, i) => {
        const [x, y] = pt(i, ing.score);
        return <circle key={i} cx={x} cy={y} r="3.5" fill="#C9A24B" />;
      })}
      {/* labels */}
      {ingredients.map((ing, i) => {
        const [x, y] = pt(i, 5.5);
        const anchor = Math.abs(x - cx) < 8 ? "middle" : x > cx ? "start" : "end";
        const parts = ing.label.includes(" & ") ? ing.label.split(" & ") : [ing.label];
        return (
          <text key={i} x={x} y={y} textAnchor={anchor} dominantBaseline="middle"
            style={{ fontFamily: "Arial, sans-serif", fontSize: "9px", letterSpacing: "1px", textTransform: "uppercase", fill: "#7a746c" }}>
            {parts.length === 1
              ? parts[0]
              : parts.map((p, j) => (
                  <tspan key={j} x={x} dy={j === 0 ? "-0.45em" : "1.05em"}>{j === 0 ? p : `& ${p}`}</tspan>
                ))}
          </text>
        );
      })}
    </svg>
  );
}

export default function DolceVitaAudit() {
  const [ratings, setRatings] = useState({});
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const setRating = (gk, pi, v) =>
    setRatings((r) => ({ ...r, [gk]: { ...(r[gk] || {}), [pi]: v } }));
  const groupDone = (gk) => Object.keys(ratings[gk] || {}).length === 3;
  const groupAvg = (gk) => {
    const vals = Object.values(ratings[gk] || {});
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  };
  const answeredQ = GROUPS.reduce((n, g) => n + Object.keys(ratings[g.key] || {}).length, 0);
  const canSubmit = answeredQ === 15 && firstName.trim() && email.trim();
  const scrollToId = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  // After results render, jump to the top so the score + wheel are seen first
  useEffect(() => {
    if (result) window.scrollTo(0, 0);
  }, [result]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please rate all fifteen prompts and add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const scores = Object.fromEntries(
        GROUPS.map((g) => [g.key, [0, 1, 2].map((i) => ratings[g.key][i])])
      );
      const res = await fetch(`${API}/audit/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          scores,
          origin: window.location.origin,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Something went wrong.");
      const data = await res.json();
      setResult(data);
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2C2A28] antialiased">
      {!result ? (
        <>
          {/* INTRO */}
          <section className="px-6 pt-14 pb-8 sm:pt-20 text-center max-w-2xl mx-auto">
            <p className="font-mono-a text-[11px] uppercase tracking-[0.25em] text-[#B85C38]">The Dolce Vita Audit</p>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">How much of the sweet life are you actually living?</h1>
            <p className="mt-6 text-base sm:text-lg text-[#595550] leading-relaxed">
              La dolce vita isn't wine, sunshine and other people's holidays — and it isn't about more time or money. It's how life actually gets in: through your senses, through beauty, through the table, through whatever's in front of you. Less a test than an invitation — to get curious, and maybe discover something useful about yourself.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                data-testid="audit-cta-why"
                onClick={() => scrollToId("why")}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#B85C38] text-[#F7F5F0] rounded-full px-8 py-4 font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#2C2A28] transition-colors"
              >
                Is this worth my time? <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-testid="audit-cta-begin"
                onClick={() => scrollToId("audit-form")}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 border border-[#2C2A28]/25 text-[#2C2A28] font-mono-a text-xs uppercase tracking-[0.2em] hover:border-[#B85C38] hover:text-[#B85C38] transition-colors"
              >
                I'm ready — begin the audit
              </button>
            </div>
            <p id="why" className="mt-12 scroll-mt-24 font-serif text-2xl sm:text-3xl tracking-tight leading-snug text-[#2C2A28]">
              You can't out-think <span className="text-[#B85C38]">depletion</span>.
            </p>
            <p className="mt-5 text-base sm:text-lg text-[#595550] leading-relaxed">
              Your creativity. Your clarity. Your intuition — that fast, quiet knowing that hands you the answer before you've reasoned your way to it. Your ability to crack the hard problem and stay steady when everything's on fire.
            </p>
            <p className="mt-4 font-serif text-xl sm:text-2xl leading-snug text-[#2C2A28]">
              It all runs on one thing: <span className="text-[#B85C38]">your energy</span>.
            </p>
            <p className="mt-5 text-base sm:text-lg text-[#595550] leading-relaxed">
              And most accomplished women are running on far less than they know. Not because they're weak — because they've <span className="text-[#B85C38] font-medium">normalized the leak</span>.
            </p>
            <div className="mt-8 flex justify-center">
              <img src="/images/logo-mark.png" alt="The Ageless Woman — radiant profile mark" className="h-24 w-24 sm:h-28 sm:w-28 object-contain opacity-90" />
            </div>
            <div className="mt-4 border-l-2 border-[#C9A24B] pl-5 py-1 text-left">
              <p className="text-base sm:text-lg text-[#595550] leading-relaxed">
                The five ingredients of la dolce vita — pleasure, connection, rest, beauty, desire — were never rewards you earn once the work is done.
              </p>
              <p className="mt-2 font-serif text-xl sm:text-2xl leading-snug text-[#2C2A28]">
                They're the <span className="text-[#B85C38]">fuel</span> the work runs on.
              </p>
            </div>
            <p className="mt-6 text-base sm:text-lg text-[#595550] leading-relaxed">
              This audit is an <span className="text-[#B85C38] font-medium">honest mirror</span>: where your vitality is draining, and how much more is available to you.
            </p>
            <p className="mt-3 font-serif text-lg sm:text-xl italic text-[#2C2A28]">
              Because you can't reclaim what you can't see.
            </p>
            <div className="mt-6 text-left bg-white/60 border hairline rounded-md px-6 py-6">
              <p className="text-base sm:text-lg text-[#595550] leading-relaxed">
                <span className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#B85C38] block mb-3">Before you begin, a quick reframe.</span>
                This isn't superfluous dolce vita fluff. When those five ingredients are fully tasted — spicing your life — energy moves freely through your system, and that <span className="text-[#B85C38] font-medium">vitality</span> becomes <span className="text-[#B85C38] font-medium">sustained performance</span>, firing the qualities your work actually runs on:
              </p>
              <ul className="mt-4 space-y-3.5 text-[15px] sm:text-base text-[#595550] leading-relaxed">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 grid place-items-center w-8 h-8 shrink-0 rounded-full bg-[#B85C38]/10 text-[#B85C38]"><Sparkles className="w-4 h-4" /></span>
                  <span><strong className="text-[#2C2A28]">Creativity</strong> — so original ideas arrive on their own instead of being forced, and the work that used to drain you starts to generate you.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 grid place-items-center w-8 h-8 shrink-0 rounded-full bg-[#B85C38]/10 text-[#B85C38]"><Shield className="w-4 h-4" /></span>
                  <span><strong className="text-[#2C2A28]">Resilience</strong> — so you think clearly under pressure instead of freezing, and meet the hard moment from steadiness rather than survival.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 grid place-items-center w-8 h-8 shrink-0 rounded-full bg-[#B85C38]/10 text-[#B85C38]"><Eye className="w-4 h-4" /></span>
                  <span><strong className="text-[#2C2A28]">Intuition</strong> — so you can make sharp decisions with little information, reading the room and the moment faster than analysis alone ever could.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 grid place-items-center w-8 h-8 shrink-0 rounded-full bg-[#B85C38]/10 text-[#B85C38]"><Compass className="w-4 h-4" /></span>
                  <span><strong className="text-[#2C2A28]">Presence</strong> — so you meet the unknown with awe and curiosity instead of fear, and treat uncertainty as territory to explore rather than threat to defend.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 grid place-items-center w-8 h-8 shrink-0 rounded-full bg-[#B85C38]/10 text-[#B85C38]"><Magnet className="w-4 h-4" /></span>
                  <span><strong className="text-[#2C2A28]">Magnetism</strong> — so the energy people can feel but can't name walks in ahead of you, and opens doors before you say a word.</span>
                </li>
              </ul>
            </div>
            <p className="mt-5 text-[15px] sm:text-base text-[#595550] leading-relaxed">
              Five ingredients, fifteen honest prompts. Give it 5–7 minutes — it asks you to think situationally, so let each one land before you answer. You'll see your score and your wheel the moment you finish.
            </p>
            <button
              type="button"
              data-testid="audit-cta-begin-2"
              onClick={() => scrollToId("audit-form")}
              className="mt-7 inline-flex items-center justify-center gap-2 bg-[#B85C38] text-[#F7F5F0] rounded-full px-9 py-4 font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#2C2A28] transition-colors"
            >
              Begin the audit <ArrowRight className="w-4 h-4" />
            </button>
          </section>

          {/* SCALE LEGEND + PROGRESS — sticky so it stays visible while answering */}
          <div className="sticky top-0 z-30 bg-[#F7F5F0]/95 backdrop-blur-md border-y hairline">
            <div className="px-6 max-w-2xl mx-auto py-3">
              <div className="flex items-center justify-between mb-2.5">
                <span className="font-mono-a text-[9px] uppercase tracking-[0.2em] text-[#B85C38]">How true is this for you?</span>
                <span className="font-mono-a text-[10px] uppercase tracking-[0.18em] text-[#7a746c]" data-testid="audit-progress-count">
                  {answeredQ} <span className="text-[#c9c1b3]">/ 15</span>
                </span>
              </div>
              <div className="grid grid-cols-5 gap-2 items-start">
                {SCALE.map((s) => (
                  <div key={s.v} className="flex flex-col items-center text-center gap-1.5">
                    <span className="grid place-items-center h-7 w-7 rounded-full border border-[#C9A24B]/60 font-serif text-[#C9A24B] text-sm leading-none">{s.v}</span>
                    <span className="text-[10px] sm:text-[11px] text-[#7a746c] leading-tight">{s.short}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 h-1 w-full rounded-full bg-[#E4DECB] overflow-hidden">
                <div
                  data-testid="audit-progress-bar"
                  className="h-full rounded-full bg-[#B85C38] transition-all duration-500 ease-out"
                  style={{ width: `${(answeredQ / 15) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* FORM */}
          <form onSubmit={submit} id="audit-form" className="px-6 pt-12 pb-24 max-w-2xl mx-auto scroll-mt-28">
            <div className="space-y-14">
              {GROUPS.map((g, gi) => (
                <Reveal key={g.key}>
                  <div data-testid={`audit-group-${g.key}`}>
                    <p className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#B85C38]">
                      {String(gi + 1).padStart(2, "0")} · {g.ingredient}
                    </p>
                    <h2 className="mt-2 font-serif text-2xl sm:text-3xl tracking-tight leading-snug">{g.subtitle}</h2>
                    <div className="mt-6 space-y-5">
                      {g.prompts.map((p, pi) => {
                        const chosen = (ratings[g.key] || {})[pi];
                        return (
                          <div key={pi} data-testid={`audit-${g.key}-q${pi}`}>
                            <p className="text-base sm:text-lg leading-relaxed text-[#3a3632]">{p}</p>
                            <div className="mt-3.5 flex items-center gap-2.5">
                              {SCALE.map((s) => {
                                const active = chosen === s.v;
                                return (
                                  <button
                                    key={s.v}
                                    type="button"
                                    title={s.label}
                                    aria-label={`${s.v} — ${s.label}`}
                                    data-testid={`audit-${g.key}-q${pi}-rating-${s.v}`}
                                    onClick={() => setRating(g.key, pi, s.v)}
                                    className={`h-12 w-12 sm:h-14 sm:w-14 rounded-full border font-serif text-xl transition-colors ${
                                      active
                                        ? "bg-[#2C2A28] border-[#2C2A28] text-[#C9A24B]"
                                        : "bg-white border-[#E4DECB] text-[#9a938a] hover:border-[#C9A24B] hover:text-[#C9A24B]"
                                    }`}
                                  >
                                    {s.v}
                                  </button>
                                );
                              })}
                              {groupDone(g.key) && pi === 2 && (
                                <span className="ml-auto font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#C9A24B]">
                                  avg {groupAvg(g.key)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* CONTACT + SUBMIT */}
            <div className="mt-16 border-t hairline pt-10">
              <p className="font-serif text-2xl tracking-tight">Where should we send your result?</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="audit-first-name"
                  type="text" placeholder="First name" value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="rounded-sm border border-[#E4DECB] bg-white px-4 py-3.5 text-[15px] outline-none focus:border-[#C9A24B]"
                />
                <input
                  data-testid="audit-email"
                  type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-sm border border-[#E4DECB] bg-white px-4 py-3.5 text-[15px] outline-none focus:border-[#C9A24B]"
                />
              </div>
              <p className="mt-3 text-xs text-[#9a938a]">{answeredQ} of 15 rated. Your two practices and a live-workshop invitation arrive by email.</p>
              <button
                data-testid="audit-submit"
                type="submit" disabled={loading}
                className="mt-5 w-full sm:w-auto bg-[#B85C38] text-[#F7F5F0] rounded-full px-10 py-4 font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#2C2A28] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {loading ? "Reading your wheel…" : <>See my Dolce Vita score <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        </>
      ) : (
        /* RESULT */
        <section className="px-6 pt-14 pb-24 max-w-2xl mx-auto" data-testid="audit-result">
          <div className="text-center">
            <p className="font-mono-a text-[11px] uppercase tracking-[0.25em] text-[#B85C38]">Your Dolce Vita Score</p>
            <div className="mt-4 font-serif tracking-tighter leading-none">
              <span className="text-7xl sm:text-8xl text-[#C9A24B]" data-testid="audit-score">{result.total}</span>
              <span className="text-3xl text-[#9a938a]"> / 25</span>
            </div>
            <h1 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight" data-testid="audit-tier">{result.tier_name}</h1>
          </div>

          <div className="mt-10">
            <Wheel ingredients={result.ingredients} />
          </div>

          <p className="mt-6 text-center font-serif text-lg text-[#2C2A28]" data-testid="audit-fullest-thinnest">
            Your fullest: <span className="text-[#C9A24B]">{result.fullest_label}</span>
            <span className="text-[#9a938a]"> · </span>
            Your thinnest: <span className="text-[#B85C38]">{result.thinnest_label}</span>
          </p>

          <div className="mt-10 space-y-5 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base border-l-2 border-[#B85C38]/40 pl-5">
            {result.tier_paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="mt-10">
            <p className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#B85C38]">And one honest note about your number.</p>
            <p className="mt-3 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base">{result.honesty_note}</p>
          </div>

          <div className="mt-12 text-center bg-[#EAE5D9]/50 border hairline rounded-sm px-6 py-8">
            <p className="font-serif text-xl leading-snug text-[#2C2A28]">{result.closing_line}</p>
            <a
              href="/workshop"
              data-testid="audit-workshop-link"
              className="mt-6 inline-flex items-center gap-2 bg-[#B85C38] text-[#F7F5F0] rounded-full px-9 py-3.5 font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#2C2A28] transition-colors"
            >
              Find out where your energy is draining <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      )}

      <footer className="border-t hairline px-6 py-10 text-center">
        <p className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#9a938a]">
          The Dolce Vita Audit
        </p>
      </footer>
    </div>
  );
}
